/**
 * A raise's logo on the public pages: our own copy at
 * /img/logos/{slug}-{size}.webp (src/raise-logo/), never the platform's
 * address. Shared by the server's view models and the pages' scripts, which
 * redraw the /raises cards from the same data.
 */

/**
 * The square WebPs kept, in pixels. The largest box is the raise page's
 * 80 px logo, so 256 is sharp on a 3× screen and 128 on most others.
 */
export const LOGO_SIZES = [128, 256] as const;
export type LogoSize = (typeof LOGO_SIZES)[number];

/**
 * What a page knows of a raise's logo as it is drawn, from our store alone
 * (RaiseLogoService.states): nothing is read from a platform for a page.
 *
 * - `stored`: in the bucket; `v` names the picture, so its address is
 *   cached for good and changes with the logo.
 * - `unread`: the platform gives one not read yet, or due another try. The
 *   address reads it when the browser asks, and answers the raise's
 *   initials while it cannot.
 * - `none`: no logo, or one that could not be read or drawn, or no bucket
 *   to keep it in: the page draws the initials itself.
 */
export type LogoState =
  { kind: 'stored'; v: string } | { kind: 'unread' } | { kind: 'none' };

export const NO_LOGO: LogoState = { kind: 'none' };

/** The logo's address at one size, with the picture's version where it is known. */
export function logoPath(slug: string, size: LogoSize, v?: string): string {
  const path = `/img/logos/${encodeURIComponent(slug)}-${size}.webp`;
  return v ? `${path}?v=${encodeURIComponent(v)}` : path;
}

/** The raise and size a file under /img/logos/ names, or null for no such file. */
export function parseLogoFile(
  file: string,
): { slug: string; size: LogoSize } | null {
  const match = /^(.+)-(128|256)\.webp$/.exec(file);
  return match ? { slug: match[1], size: Number(match[2]) as LogoSize } : null;
}

/** An <img>'s src, srcset and alt; the template gives its box's size. */
export interface LogoImg {
  src: string;
  srcset: string;
  alt: string;
}

/** The raise's logo as an <img>, or null where the page draws its initials. */
export function logoImg(raise: {
  slug: string;
  name: string;
  logo: LogoState;
}): LogoImg | null {
  if (raise.logo.kind === 'none') return null;
  const v = raise.logo.kind === 'stored' ? raise.logo.v : undefined;
  return {
    src: logoPath(raise.slug, 128, v),
    srcset: LOGO_SIZES.map((s) => `${logoPath(raise.slug, s, v)} ${s}w`).join(
      ', ',
    ),
    alt: `${raise.name} logo`,
  };
}
